// frontend/src/pages/ItemDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatCurrency, IMAGE_BASE_URL } from '../services/utils';
import { defaultAvatars } from '../services/avatars';
import { useFavorites } from '../context/FavoritesContext';
import ReportModal from '../components/ReportModal';

const placeholderImage = 'https://placehold.co/600x400?text=Mercado+Jopoi';
const defaultAvatar = defaultAvatars[0];

function formatDate(dateString) {
    if (!dateString) return 'Fecha desconocida';
    try {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    } catch (e) { return 'Fecha invalida'; }
}

function ItemDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const { favoriteIds, addFavorite, removeFavorite, authChecked } = useFavorites();
    const token = sessionStorage.getItem('token');

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [nuevaPregunta, setNuevaPregunta] = useState('');
    const [isSendingPregunta, setIsSendingPregunta] = useState(false);
    const [respuestas, setRespuestas] = useState({});
    const [isSendingRespuesta, setIsSendingRespuesta] = useState(null);
    const [respuestaStatus, setRespuestaStatus] = useState({});
    const [isStartingTransaction, setIsStartingTransaction] = useState(false);

    // Reporte
    const [showReportModal, setShowReportModal] = useState(false);

    const fetchItemData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const itemResponse = await api.get(`/items/${id}`);
            setItem(itemResponse.data);
            if (token) {
                const userResponse = await api.get('/users/me');
                setCurrentUser(userResponse.data);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setCurrentUser(null);
                sessionStorage.removeItem('token');
            } else {
                setError(err.response?.data?.detail || 'No se pudo cargar el producto.');
            }
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => { fetchItemData(); }, [fetchItemData]);

    const handleComprar = async () => {
        if (!token) { navigate('/login'); return; }
        if (currentUser?.id === item?.propietario?.id) { alert('No puedes comprar tu propio producto.'); return; }
        setIsStartingTransaction(true);
        setError(null);
        try {
            await api.post('/transacciones/iniciar', { item_id: item.id });
            navigate(`/mensajes?item=${item.id}&usuario=${item.propietario.id}`);
        } catch (err) {
            setError(err.response?.data?.detail || 'No se pudo iniciar la compra.');
        } finally {
            setIsStartingTransaction(false);
        }
    };

    const handleContactar = () => {
        if (!token) { navigate('/login'); return; }
        navigate(`/mensajes?item=${item.id}&usuario=${item.propietario.id}`);
    };

    const handlePreguntaSubmit = async (e) => {
        e.preventDefault();
        if (!nuevaPregunta.trim() || !token) return;
        setIsSendingPregunta(true);
        try {
            await api.post('/preguntas/', { texto: nuevaPregunta, item_id: item.id });
            setNuevaPregunta('');
            await fetchItemData();
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al enviar la pregunta.');
        } finally {
            setIsSendingPregunta(false);
        }
    };

    const handleRespuestaSubmit = async (e, preguntaId) => {
        e.preventDefault();
        const textoRespuesta = respuestas[preguntaId];
        if (!textoRespuesta?.trim()) return;
        setIsSendingRespuesta(preguntaId);
        try {
            await api.put(`/preguntas/${preguntaId}/responder`, { texto: textoRespuesta });
            setRespuestaStatus(prev => ({ ...prev, [preguntaId]: '¡Respuesta enviada!' }));
            await fetchItemData();
        } catch (err) {
            setRespuestaStatus(prev => ({ ...prev, [preguntaId]: 'Error al enviar.' }));
        } finally {
            setIsSendingRespuesta(null);
        }
    };

    const handleToggleFavorite = () => {
        if (!authChecked || !token) { navigate('/login'); return; }
        favoriteIds.has(item?.id) ? removeFavorite(item.id) : addFavorite(item);
    };

    if (loading) return <div className="text-center mt-5">Cargando producto...</div>;
    if (error && !item) return <div className="alert alert-danger mt-4">{error}</div>;
    if (!item) return <div className="alert alert-warning mt-4">Producto no encontrado.</div>;

    const isOwner = currentUser?.id === item?.propietario?.id;
    const isFavorite = favoriteIds.has(item.id);
    const esServicio = item.tipo_operacion === 'Servicio';
    const imageUrl = item.imagen_filename ? `${IMAGE_BASE_URL}${item.imagen_filename}` : placeholderImage;
    const avatarUrl = item.propietario?.avatar_url || defaultAvatar;

    // Texto del botón según tipo
    const buttonText = isStartingTransaction ? 'Iniciando...' :
        esServicio ? 'Contratar servicio' :
        item.tipo_operacion === 'Préstamo' ? '¡Lo quiero! (Préstamo)' :
        '¡Lo quiero! (Comprar)';

    return (
        <div className="container mt-4 mb-5">
            <div className="row g-5">

                {/* ── Columna izquierda ── */}
                <div className="col-lg-8">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h1 className="mb-1">{item.titulo}</h1>
                            {/* Badge tipo operación */}
                            <span className={`badge me-2 ${
                                esServicio ? 'bg-success' :
                                item.tipo_operacion === 'Préstamo' ? 'bg-info text-dark' :
                                'bg-primary'
                            }`}>
                                {esServicio && <i className="bi bi-tools me-1"></i>}
                                {item.tipo_operacion}
                            </span>
                            {!esServicio && (
                                <span className="badge bg-secondary">{item.estado}</span>
                            )}
                        </div>
                        <button
                            className={`btn btn-lg ${isFavorite ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={handleToggleFavorite}
                            disabled={!authChecked || isOwner}
                            title={isOwner ? 'No puedes añadir tu propio item' : 'Favoritos'}
                        >
                            <i className={`bi ${isFavorite ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                        </button>
                    </div>

                    <img
                        src={imageUrl}
                        className="img-fluid rounded shadow-sm mb-4 w-100"
                        alt={item.titulo}
                        style={{ maxHeight: '600px', objectFit: 'cover' }}
                    />

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title">
                                {esServicio ? 'Sobre el servicio' : 'Descripción'}
                            </h5>
                            <p className="card-text">
                                {item.descripcion || (esServicio
                                    ? 'El prestador no añadió una descripción.'
                                    : 'El vendedor no ha añadido una descripción.')}
                            </p>
                            <hr />
                            <div className="row">
                                <div className="col-md-4">
                                    <strong>Precio{esServicio ? ' base' : ''}:</strong> {formatCurrency(item.precio)}
                                </div>
                                <div className="col-md-4">
                                    <strong>Tipo:</strong> {item.tipo_operacion}
                                </div>
                                {!esServicio && (
                                    <div className="col-md-4">
                                        <strong>Estado:</strong> {item.estado}
                                    </div>
                                )}
                            </div>
                            {item.facultad && (
                                <p className="mt-3 mb-0 text-muted small">
                                    <i className="bi bi-geo-alt-fill me-1"></i>
                                    {item.facultad.nombre}, {item.facultad.universidad?.siglas || 'Universidad desconocida'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Columna derecha ──
                    FIX: un único div sticky que contiene AMBOS cards.
                    Así precio y perfil se mueven juntos al hacer scroll.
                */}
                <div className="col-lg-4">
                    <div className="position-sticky" style={{ top: '80px' }}>

                        {/* Card acciones */}
                        <div className="card shadow-sm mb-3">
                            <div className="card-body">
                                <h4 className="card-title mb-3">{formatCurrency(item.precio)}</h4>
                                {error && <div className="alert alert-danger small">{error}</div>}
                                <div className="d-grid gap-2">
                                    {authChecked && isOwner && (
                                        <Link to={`/items/${item.id}/editar`} className="btn btn-secondary btn-lg">
                                            Editar mi Publicación
                                        </Link>
                                    )}
                                    {authChecked && !isOwner && (
                                        <button
                                            className="btn btn-primary btn-lg"
                                            onClick={handleComprar}
                                            disabled={!item.disponible || isStartingTransaction}
                                        >
                                            {item.disponible ? buttonText : 'No disponible'}
                                        </button>
                                    )}
                                    {authChecked && !isOwner && (
                                        <button className="btn btn-outline-secondary" onClick={handleContactar}>
                                            <i className="bi bi-chat-dots me-2"></i>
                                            {esServicio ? 'Consultar al prestador' : 'Contactar con Vendedor'}
                                        </button>
                                    )}
                                    {!token && (
                                        <button className="btn btn-primary btn-lg" onClick={() => navigate('/login')}>
                                            Iniciar sesión para {esServicio ? 'contratar' : 'comprar'}
                                        </button>
                                    )}
                                </div>
                                {!item.disponible && (
                                    <div className="alert alert-warning mt-3 mb-0 small text-center">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        Este item ya no está disponible
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Card perfil vendedor */}
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <h5 className="card-title mb-3">
                                    {esServicio ? 'Ofrecido por' : 'Vendido por'}
                                </h5>
                                {item.propietario ? (
                                    <Link to={`/perfil/${item.propietario.id}`} className="text-decoration-none text-dark">
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={avatarUrl}
                                                alt={item.propietario.username}
                                                className="rounded-circle me-3"
                                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                            />
                                            <div>
                                                <h6 className="mb-0">{item.propietario.username}</h6>
                                                <span className="text-muted small">
                                                    Se unió en {formatDate(item.propietario.fecha_registro)}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ) : (
                                    <p className="text-muted">Vendedor no disponible</p>
                                )}

                                {/* Botón reportar publicación — solo para no-dueños logueados */}
                                {authChecked && !isOwner && token && (
                                    <div className="mt-3 pt-3 border-top">
                                        <button
                                            className="btn btn-sm btn-outline-danger w-100"
                                            onClick={() => setShowReportModal(true)}
                                        >
                                            <i className="bi bi-flag me-1"></i>
                                            Reportar publicación
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>{/* fin position-sticky */}
                </div>
            </div>

            <hr className="my-5" />

            {/* ── Preguntas y Respuestas ── */}
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h4 className="card-title mb-4">Preguntas y Respuestas</h4>

                            {authChecked && !isOwner && token && (
                                <form onSubmit={handlePreguntaSubmit} className="mb-4">
                                    <textarea
                                        className="form-control mb-2"
                                        rows="3"
                                        value={nuevaPregunta}
                                        onChange={e => setNuevaPregunta(e.target.value)}
                                        placeholder="Escribi tu consulta pública..."
                                        disabled={isSendingPregunta}
                                    />
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSendingPregunta || !nuevaPregunta.trim()}
                                    >
                                        {isSendingPregunta ? 'Enviando...' : 'Enviar Pregunta'}
                                    </button>
                                </form>
                            )}

                            {item.preguntas && item.preguntas.length > 0 ? (
                                item.preguntas.map(p => {
                                    if (!p?.id) return null;
                                    const preguntaAvatarUrl = p.usuario?.avatar_url || defaultAvatar;
                                    const nombreUsuario = p.usuario?.username || 'Usuario Desconocido';

                                    return (
                                        <div key={p.id} className="mb-3 border-bottom pb-3">
                                            <div className="d-flex align-items-start mb-2">
                                                <img src={preguntaAvatarUrl} alt={nombreUsuario} className="rounded-circle me-3"
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                <div className="flex-grow-1">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted small fw-bold">
                                                            {p.usuario?.id
                                                                ? <Link to={`/perfil/${p.usuario.id}`} className="text-decoration-none">{nombreUsuario}</Link>
                                                                : nombreUsuario}
                                                        </span>
                                                        <span className="text-muted small">{formatDate(p.fecha_pregunta)}</span>
                                                    </div>
                                                    <p className="mb-1 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{p.texto}</p>
                                                </div>
                                            </div>

                                            {p.respuesta ? (
                                                <div style={{ marginLeft: '48px' }}>
                                                    <div className="d-flex align-items-start">
                                                        <img src={avatarUrl} alt="Vendedor" className="rounded-circle me-3"
                                                            style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex justify-content-between">
                                                                <span className="text-muted small fw-bold">
                                                                    {esServicio ? 'Prestador' : 'Vendedor'}
                                                                </span>
                                                                <span className="text-muted small">{formatDate(p.fecha_respuesta)}</span>
                                                            </div>
                                                            <p className="mb-0 mt-1 fst-italic" style={{ whiteSpace: 'pre-wrap' }}>{p.respuesta}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : isOwner && (
                                                <form style={{ marginLeft: '48px' }} onSubmit={e => handleRespuestaSubmit(e, p.id)}>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows="2"
                                                        placeholder="Escribi tu respuesta..."
                                                        value={respuestas[p.id] || ''}
                                                        onChange={e => setRespuestas(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                        disabled={isSendingRespuesta === p.id}
                                                    />
                                                    <div className="d-flex align-items-center gap-2 mt-2">
                                                        <button type="submit" className="btn btn-sm btn-outline-primary"
                                                            disabled={isSendingRespuesta === p.id || !(respuestas[p.id] || '').trim()}>
                                                            {isSendingRespuesta === p.id ? 'Enviando...' : 'Responder'}
                                                        </button>
                                                        {respuestaStatus[p.id] && (
                                                            <small className={respuestaStatus[p.id].startsWith('Error') ? 'text-danger' : 'text-success'}>
                                                                {respuestaStatus[p.id]}
                                                            </small>
                                                        )}
                                                    </div>
                                                </form>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-muted">Todavía no hay preguntas.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal reporte publicación */}
            <ReportModal
                show={showReportModal}
                onHide={() => setShowReportModal(false)}
                itemId={item.id}
                nombre={item.titulo}
            />
        </div>
    );
}

export default ItemDetailPage;
